import { useQueries, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import {
  CARD_TRANSACTIONS_PAGE_SIZE,
  getCardTransactions,
  type CardTransaction,
  type TransactionsPage,
} from '../api/client.ts';

import { mergePagedFeeds, type PagedFeed } from './merge-pages.ts';

const TXNS_QUERY_ROOT = 'txns-page';

const timeOf = (tx: CardTransaction): number => new Date(tx.createdAt).getTime();
const keyOf = (tx: CardTransaction): string => tx.id;

interface PagePlan {
  readonly cardId: string;
  readonly page: number;
}

interface CardAccumulator {
  readonly items: CardTransaction[];
  loaded: number;
  lastLoadedPage: number;
  lastPageSize: number;
  total: number | undefined;
  pending: boolean;
  error: boolean;
  firstPagePending: boolean;
  firstPageError: boolean;
}

export interface CardTransactionsFeed {
  // Newest first, merged across cards with no chronological holes.
  readonly transactions: readonly CardTransaction[];
  readonly isLoadingFirstPage: boolean;
  readonly isLoadingMore: boolean;
  readonly isFirstPageError: boolean;
  readonly isPageError: boolean;
  readonly hasMore: boolean;
  readonly loadMore: () => void;
  readonly retry: () => void;
}

interface FeedState {
  readonly transactions: readonly CardTransaction[];
  readonly isLoadingFirstPage: boolean;
  readonly isLoadingMore: boolean;
  readonly isFirstPageError: boolean;
  readonly isPageError: boolean;
  readonly hasMore: boolean;
  // Cards that both pin the merge boundary and are ready to fetch another page.
  readonly nextCardIds: readonly string[];
}

const emptyAccumulator = (): CardAccumulator => ({
  items: [],
  loaded: 0,
  lastLoadedPage: 0,
  lastPageSize: 0,
  total: undefined,
  pending: false,
  error: false,
  firstPagePending: false,
  firstPageError: false,
});

// An empty page always ends the feed, whatever total claims — otherwise a
// stale or wrong total would have the list ask for page after page forever.
const hasUnfetchedPages = (acc: CardAccumulator): boolean => {
  if (acc.lastPageSize === 0) return false;
  if (acc.total !== undefined) return acc.loaded < acc.total;
  return acc.lastPageSize >= CARD_TRANSACTIONS_PAGE_SIZE;
};

const summarise = (
  cardIds: readonly string[],
  plans: readonly PagePlan[],
  results: readonly UseQueryResult<TransactionsPage>[],
): FeedState => {
  const byCard = new Map<string, CardAccumulator>(
    cardIds.map((id) => [id, emptyAccumulator()] as const),
  );

  plans.forEach((plan, index) => {
    const result = results[index];
    const acc = byCard.get(plan.cardId);
    if (result === undefined || acc === undefined) return;
    if (result.isSuccess) {
      acc.items.push(...result.data.data);
      acc.loaded += result.data.data.length;
      if (plan.page >= acc.lastLoadedPage) {
        acc.lastLoadedPage = plan.page;
        acc.lastPageSize = result.data.data.length;
      }
      if (result.data.total !== undefined) acc.total = result.data.total;
      return;
    }
    if (result.isError) {
      acc.error = true;
      if (plan.page === 1) acc.firstPageError = true;
      return;
    }
    acc.pending = true;
    if (plan.page === 1) acc.firstPagePending = true;
  });

  const accumulators = cardIds.map((id) => byCard.get(id) ?? emptyAccumulator());
  // A page in flight or a page that failed keeps the feed "unfinished" so the
  // merge boundary holds — the list must not briefly claim it has reached the
  // oldest row just because the request that proves otherwise is outstanding.
  const feeds: PagedFeed<CardTransaction>[] = accumulators.map((acc) => ({
    items: acc.items,
    hasMore: acc.pending || acc.error || hasUnfetchedPages(acc),
  }));

  const merged = mergePagedFeeds(feeds, { timeOf, keyOf });
  const isLoadingFirstPage = accumulators.some((acc) => acc.firstPagePending);

  return {
    transactions: merged.items,
    isLoadingFirstPage,
    isLoadingMore: !isLoadingFirstPage && accumulators.some((acc) => acc.pending),
    isFirstPageError: accumulators.some((acc) => acc.firstPageError),
    isPageError: accumulators.some((acc) => acc.error),
    hasMore: !merged.exhausted,
    nextCardIds: merged.limiting
      .filter((index) => {
        const acc = accumulators[index];
        return acc !== undefined && !acc.pending && !acc.error && hasUnfetchedPages(acc);
      })
      .map((index) => cardIds[index] ?? ''),
  };
};

// Pages every card in step. Only the card pinning the merge boundary is asked
// for another page, so the combined list grows chronologically instead of each
// card racing ahead on its own timeline.
export const useCardTransactions = (cardIds: readonly string[]): CardTransactionsFeed => {
  const queryClient = useQueryClient();
  const [pagesByCard, setPagesByCard] = useState<Readonly<Record<string, number>>>({});

  const plans = useMemo<readonly PagePlan[]>(
    () =>
      cardIds.flatMap((cardId) =>
        Array.from({ length: pagesByCard[cardId] ?? 1 }, (_, i) => ({ cardId, page: i + 1 })),
      ),
    [cardIds, pagesByCard],
  );

  const state = useQueries({
    queries: plans.map((plan) => ({
      queryKey: [TXNS_QUERY_ROOT, plan.cardId, plan.page, CARD_TRANSACTIONS_PAGE_SIZE],
      queryFn: ({ signal }: { signal: AbortSignal }): Promise<TransactionsPage> =>
        getCardTransactions(plan.cardId, plan.page, CARD_TRANSACTIONS_PAGE_SIZE, signal),
      staleTime: 30_000,
      // Refetch cost here scales with how far the user has scrolled: a focus
      // event would re-request every loaded page of every card at once, which
      // for a deep list is a burst big enough to trip the per-user cap. Entering
      // the screen still refetches, which is when a new transaction matters.
      refetchOnWindowFocus: false,
    })),
    combine: (results: readonly UseQueryResult<TransactionsPage>[]): FeedState =>
      summarise(cardIds, plans, results),
  });

  const { nextCardIds } = state;
  // Targets come from this render's page counts rather than from the updater's
  // prev, so a sentinel that fires twice before React re-renders still asks
  // for the same next page instead of jumping two pages ahead.
  const loadMore = useCallback((): void => {
    if (nextCardIds.length === 0) return;
    setPagesByCard((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const id of nextCardIds) {
        const target = (pagesByCard[id] ?? 1) + 1;
        if ((prev[id] ?? 1) >= target) continue;
        next[id] = target;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [nextCardIds, pagesByCard]);

  const retry = useCallback((): void => {
    void queryClient.refetchQueries({
      type: 'active',
      predicate: (query) => query.queryKey[0] === TXNS_QUERY_ROOT && query.state.status === 'error',
    });
  }, [queryClient]);

  return {
    transactions: state.transactions,
    isLoadingFirstPage: state.isLoadingFirstPage,
    isLoadingMore: state.isLoadingMore,
    isFirstPageError: state.isFirstPageError,
    isPageError: state.isPageError,
    hasMore: state.hasMore,
    loadMore,
    retry,
  };
};
